import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SiteFooter from "@/components/SiteFooter";
import { Card } from "@/components/ui/card";
import { api, AUTH } from "@/lib/api";

const SURFACE_COPY = {
  merchant: {
    label: "merchant account",
    heading: "Merchant dashboards",
    empty: "No merchant businesses are linked to this account yet.",
  },
  service_provider: {
    label: "provider account",
    heading: "Provider dashboards",
    empty: "No service-provider businesses are linked to this account yet.",
  },
  auto: {
    label: "auto account",
    heading: "Auto repair dashboards",
    empty: "No auto repair businesses are linked to this account yet.",
  },
  all: {
    label: "your account",
    heading: "Businesses",
    empty: "No businesses are linked to this account yet. Contact support to connect your subscription.",
  },
};

export default function Account({ preferredCategory = null, preferredVertical = null }) {
  const nav = useNavigate();
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    if (!AUTH.token()) { nav("/login"); return; }
    api.get("/tenants").then(r => setTenants(r.data)).catch(()=>setTenants([]));
  }, [nav]);

  const visibleTenants = tenants.filter(t => {
    if (preferredVertical && (t.vertical_key || "general") !== preferredVertical) return false;
    if (preferredCategory && (t.business_category || "merchant") !== preferredCategory) return false;
    return true;
  });

  useEffect(() => {
    if (visibleTenants.length === 1) {
      nav(`/dashboard/${visibleTenants[0].slug}`, { replace: true });
    }
  }, [nav, visibleTenants]);

  const user = AUTH.user();
  if (!user) return null;
  const copy = SURFACE_COPY[preferredVertical || preferredCategory || "all"];

  return (
    <div>
      <section className="ds-container py-12">
        <p className="overline mb-3">{copy.label}</p>
        <h1 className="text-5xl mb-2">Hey, <span className="font-script text-primary text-6xl font-normal">{user.name.split(" ")[0]}</span>.</h1>
        <p className="text-muted-foreground mb-10">Your businesses. Your dashboards. One place.</p>

        <h2 className="text-2xl mb-5">{copy.heading}</h2>

        {visibleTenants.length === 0 && (
          <Card className="p-10 text-center">
            <p className="text-muted-foreground">{copy.empty}</p>
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          {visibleTenants.map(t => (
            <Link
              key={t.id}
              to={`/dashboard/${t.slug}`}
              className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={`Open dashboard for ${t.name}`}
              data-testid={`account-business-${t.slug}`}
            >
              <Card className="p-6 h-full transition-colors hover:border-primary/60 hover:bg-primary/[0.03]">
                <div className="mb-3">
                  <h3 className="text-xl mb-1">{t.name}</h3>
                  <p className="text-xs text-muted-foreground font-mono">/{t.slug}</p>
                </div>
                <div className="text-xs text-muted-foreground capitalize">{t.vertical_key === "auto" ? "Auto repair" : t.business_category === "service_provider" ? "Provider" : "Merchant"} · {t.business_type} · {t.brand_style?.replace("_"," ")}</div>
              </Card>
            </Link>
          ))}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
